import AddIcon from '@mui/icons-material/Add';
import { useGetUsersQuery } from 'common/api/endpoints/user';
import { handleError } from 'common/api/utils/handleError';
import {
  DataTable,
  DataTableMobile,
  DataTableProps,
} from 'common/components/DataTable';
import { usePSFQuery } from 'common/hooks';
import useWindowSize from 'common/hooks/useWindowSize';
import { PaginatedResult, User } from 'common/models';
import { mobile } from 'common/styles/breakpoints';
import { userTableFilters } from 'features/user-views/filters';
import { FC, useEffect, useMemo } from 'react';
import { Constants } from 'utils/constants';
import { PSFKeys, usePSFState } from 'utils/hooks/usePSFState';
import { AddUserBtn } from '../components/styles';
import { UserTableItem, useUserTableData } from '../hooks/useUserTableData';
import { useUserTableDataMobile } from '../hooks/useUserTableDataMobile';

export const UserListView: FC = () => {
  const { query, setQuery } = usePSFState(PSFKeys.Users);

  const {
    data,
    isLoading,
    isFetching,
    pagination,
    applyFilters,
    filters,
    error: getUsersError,
  } = usePSFQuery<PaginatedResult<User>>(
    useGetUsersQuery,
    {},
    {
      ...query,
    },
    setQuery,
  );

  const users = useMemo(() => data?.results ?? [], [data]);
  const { columns, data: tableData } = useUserTableData(users);
  const columnsMobile = useUserTableDataMobile();

  const { width } = useWindowSize();
  const isMobile = width < parseInt(mobile, 10);

  const buildDetailsLink = (id: number) =>
    `${
      Constants.routes.SETTINGS +
      Constants.routes.USERS_LIST +
      Constants.routes.DETAILS
    }/${id}`;

  const addUserLink =
    Constants.routes.SETTINGS +
    Constants.routes.USERS_LIST +
    Constants.routes.ADD;

  const tableProps: DataTableProps<UserTableItem> = {
    title: 'Users',
    unitToPaginate: 'users',
    isLoading,
    isFetching,
    columns: isMobile ? columnsMobile : columns,
    data: tableData,
    pagination,
    onRowClick: user => buildDetailsLink(user.id),
    filterBy: {
      filterParams: userTableFilters,
      selectedFilters: filters,
      applyFilters,
    },
    toolBar: (
      <AddUserBtn to={addUserLink}>
        <AddIcon className='add-icon' />
        Add User
      </AddUserBtn>
    ),
    loadError: !!getUsersError,
  };

  useEffect(() => {
    if (getUsersError) {
      handleError(getUsersError, 'Unable to load users');
    }
  }, [getUsersError, pagination.page]);

  return isMobile ? (
    <DataTableMobile<UserTableItem> {...tableProps} />
  ) : (
    <DataTable<UserTableItem> {...tableProps} />
  );
};
